/**
 * UI Store
 * 
 * Manages UI state with selective Supabase persistence for user preferences.
 * 
 * PERSISTED (to user_preferences table):
 * - sidebarOpen
 * - showDriftAlerts
 * - theme (future)
 * 
 * EPHEMERAL (not persisted):
 * - activeSpace, sessionMode, activeModal, notifications
 */

import { create } from 'zustand';
import type { SystemModelingSpace } from '../../types/prioritization/spaces';
import {
  getSafeSupabaseClient,
  isSupabaseAvailable,
} from '../../lib/supabaseSync';
import { useUserStore } from './userStore';

const TABLE_NAME = 'user_preferences';

interface UserPreferences {
  sidebarOpen: boolean;
  showDriftAlerts: boolean;
  theme: 'light' | 'dark' | 'system';
  defaultView?: string;
}

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: Date;
  duration?: number; // Auto-dismiss after ms
}

interface UIState {
  // Active space (ephemeral)
  activeSpace: SystemModelingSpace | null;
  setActiveSpace: (space: SystemModelingSpace | null) => void;
  
  // Session mode (ephemeral)
  sessionMode: boolean;
  sessionStartedAt: Date | null;
  sessionFocusListId: string | null;
  startSession: (focusListId: string) => void;
  endSession: () => void;
  toggleSessionMode: () => void;
  
  // Sidebar state (persisted)
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  
  // Modals and panels (ephemeral)
  activeModal: string | null;
  openModal: (modalId: string) => void;
  closeModal: () => void;
  
  // Notifications/toasts (ephemeral)
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  
  // Drift alerts visibility (persisted)
  showDriftAlerts: boolean;
  toggleDriftAlerts: () => void;
  
  // Theme (persisted)
  theme: 'light' | 'dark' | 'system';
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  
  // Supabase sync
  fetchPreferencesFromSupabase: () => Promise<void>;
  savePreferencesToSupabase: () => Promise<void>;
  
  // Loading state
  preferencesLoading: boolean;
}

// Helper to get current user ID
function getCurrentUserId(): string | null {
  const userState = useUserStore.getState();
  const id = userState.userId;
  return id && id !== 'unknown' ? id : null;
}

// Helper to save preferences
async function savePreferences(prefs: Partial<UserPreferences>): Promise<void> {
  if (!isSupabaseAvailable()) return;
  
  const userId = getCurrentUserId();
  if (!userId) {
    console.log('[UIStore] No user ID, skipping preference save');
    return;
  }
  
  try {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    
    const { error } = await supabase
      .from(TABLE_NAME)
      .upsert({
        user_id: userId,
        sidebar_open: prefs.sidebarOpen,
        show_drift_alerts: prefs.showDriftAlerts,
        theme: prefs.theme,
        default_view: prefs.defaultView,
      }, { onConflict: 'user_id' });
    
    if (error) {
      console.error('[UIStore] Failed to save preferences:', error);
    }
  } catch (err) {
    console.error('[UIStore] Save preferences exception:', err);
  }
}

export const useUIStore = create<UIState>((set, get) => ({
  activeSpace: null,
  setActiveSpace: (space) => set({ activeSpace: space }),
  
  sessionMode: false,
  sessionStartedAt: null,
  sessionFocusListId: null,
  
  startSession: (focusListId) => set({
    sessionMode: true,
    sessionStartedAt: new Date(),
    sessionFocusListId: focusListId
  }),
  
  endSession: () => set({
    sessionMode: false,
    sessionStartedAt: null,
    sessionFocusListId: null
  }),
  
  toggleSessionMode: () => set((state) => ({
    sessionMode: !state.sessionMode,
    sessionStartedAt: !state.sessionMode ? new Date() : null
  })),
  
  sidebarOpen: true,
  toggleSidebar: () => {
    const newValue = !get().sidebarOpen;
    set({ sidebarOpen: newValue });
    savePreferences({ sidebarOpen: newValue, showDriftAlerts: get().showDriftAlerts, theme: get().theme });
  },
  setSidebarOpen: (open) => {
    set({ sidebarOpen: open });
    savePreferences({ sidebarOpen: open, showDriftAlerts: get().showDriftAlerts, theme: get().theme });
  },
  
  activeModal: null,
  openModal: (modalId) => set({ activeModal: modalId }),
  closeModal: () => set({ activeModal: null }),
  
  notifications: [],
  
  addNotification: (notificationData) => {
    const notification: Notification = {
      ...notificationData,
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    };
    
    set((state) => ({
      notifications: [...state.notifications, notification]
    }));
    
    // Auto-dismiss if duration specified
    if (notification.duration) {
      setTimeout(() => {
        get().removeNotification(notification.id);
      }, notification.duration);
    }
  },
  
  removeNotification: (id) => set((state) => ({
    notifications: state.notifications.filter(n => n.id !== id)
  })),
  
  clearNotifications: () => set({ notifications: [] }),
  
  showDriftAlerts: true,
  toggleDriftAlerts: () => {
    const newValue = !get().showDriftAlerts;
    set({ showDriftAlerts: newValue });
    savePreferences({ sidebarOpen: get().sidebarOpen, showDriftAlerts: newValue, theme: get().theme });
  },
  
  theme: 'system',
  setTheme: (theme) => {
    set({ theme });
    savePreferences({ sidebarOpen: get().sidebarOpen, showDriftAlerts: get().showDriftAlerts, theme });
  },
  
  preferencesLoading: false,
  
  // Fetch preferences from Supabase
  fetchPreferencesFromSupabase: async () => {
    if (!isSupabaseAvailable()) {
      console.log('[UIStore] Supabase not available');
      return;
    }
    
    const userId = getCurrentUserId();
    if (!userId) {
      console.log('[UIStore] No user ID, skipping preference fetch');
      return;
    }
    
    set({ preferencesLoading: true });
    
    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;
      
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('[UIStore] Fetch preferences error:', error);
        set({ preferencesLoading: false });
        return;
      }
      
      if (data) {
        set({
          sidebarOpen: data.sidebar_open ?? true,
          showDriftAlerts: data.show_drift_alerts ?? true,
          theme: (data.theme as 'light' | 'dark' | 'system') ?? 'system',
          preferencesLoading: false,
        });
        console.log('[UIStore] Loaded user preferences');
      } else {
        set({ preferencesLoading: false });
      }
    } catch (err) {
      console.error('[UIStore] Fetch preferences exception:', err);
      set({ preferencesLoading: false });
    }
  },
  
  // Save all preferences to Supabase
  savePreferencesToSupabase: async () => {
    const state = get();
    await savePreferences({
      sidebarOpen: state.sidebarOpen,
      showDriftAlerts: state.showDriftAlerts,
      theme: state.theme,
    });
  },
}));

